import { useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IDENTITIES = [
  { key: "lgbtq", icon: "🏳️‍🌈", title: "Pride Lifter", description: "Identify as LGBTQ+" },
  { key: "black_brown", icon: "✊🏿", title: "Heritage Strong", description: "Black or Brown athlete" },
  { key: "neurodivergent", icon: "🧠", title: "Mind Diverse", description: "Neurodivergent / Autistic / ADHD" },
  { key: "disabled", icon: "♿", title: "Adaptive Athlete", description: "Disabled / Adaptive lifter" },
];

export default function PrideBadges({ claimed = [], onChange }) {
  const [busy, setBusy] = useState(null);
  const set = new Set(claimed);

  const toggle = async (key, isOn) => {
    setBusy(key);
    try {
      const axios = (await import("axios")).default;
      const { data } = await axios.post(`${API}/auth/pride-claim`, {
        identity_key: key,
        claim: !isOn,
      });
      onChange?.(data);
      if (!isOn) {
        toast.success("Badge claimed!", {
          description: `Welcome to the ${IDENTITIES.find((i) => i.key === key)?.title} circle.`,
          style: {
            background: "linear-gradient(135deg, #1a0d2b 0%, #0F0F0F 100%)",
            border: "2px solid #D4AF37",
            color: "#fff",
          },
        });
      }
    } catch {
      toast.error("Could not update identity.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="pride-badges-section"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 mt-6"
    >
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 inline-flex items-center gap-1.5">
          <Heart size={11} fill="#D4AF37" /> Identity & Pride
        </div>
        <h2 className="font-display text-3xl text-white">
          Wear It <span className="text-[#D4AF37]">Loud.</span>
        </h2>
        <p className="text-xs text-neutral-400 mt-1 max-w-md">
          Self-identify and unlock your community badge. Fully opt-in. Show up
          as your full, holistic self — Mind, Body & Soul.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {IDENTITIES.map((id) => {
          const on = set.has(id.key);
          return (
            <button
              key={id.key}
              data-testid={`pride-toggle-${id.key}`}
              onClick={() => toggle(id.key, on)}
              disabled={busy === id.key}
              className={`text-left p-4 border-2 transition-colors disabled:opacity-50 flex items-center gap-3 ${
                on
                  ? "border-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-zinc-800 bg-[#0A0A0A] hover:border-zinc-700"
              }`}
            >
              <span className="text-3xl flex-shrink-0">{id.icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-display text-base text-white">
                  {id.title}
                </span>
                <span className="block text-xs text-neutral-400 mt-0.5">
                  {id.description}
                </span>
              </span>
              <span
                className={`flex-shrink-0 text-[10px] uppercase tracking-widest font-bold ${
                  on ? "text-[#D4AF37]" : "text-neutral-500"
                }`}
              >
                {on ? "Claimed" : "Claim"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
